import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  alpha,
  useMediaQuery,
  useTheme,
  createTheme,
  ThemeProvider,
} from "@mui/material";

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [showPricing, setShowPricing] = useState(false);
  const [animationStart, setAnimationStart] = useState(false);

  const handleGetStarted = () => {
    setAnimationStart(true);
  };

  const pricingPlans = [
    {
      name: "Basic",
      price: "₹999",
      features: [
        "2,000 customers",
        "Basic analytics",
        "Chatbot builders, Forms",
        "Email support",
      ],
      color: theme.palette.primary.light,
    },
    {
      name: "Starter",
      price: "₹2,499",
      features: [
        "5,000 customers",
        "Advanced analytics",
        "Priority support",
        "CRM integration",
      ],
      color: theme.palette.secondary.main,
      popular: true,
    },
    {
      name: "Enterprise",
      price: "₹8,999",
      features: [
        "Unlimited customers",
        "Dedicated account manager",
        "Campaign Optimizations",
        "24/7 support",
        "Custom integrations",
        "AI-powered insights",
      ],
      color: theme.palette.primary.dark,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "primary.main",
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Curtain Animation */}
      <AnimatePresence>
        {animationStart && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "100vh" }}
            exit={{ y: "-100vh" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#0a1929",
              zIndex: 10,
            }}
            onAnimationComplete={() => setShowPricing(true)}
          />
        )}
      </AnimatePresence>

      {/* Pricing Plans */}
      {showPricing ? (
        <Container
          maxWidth="lg"
          sx={{
            py: 10,
            position: "relative",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={plan.name}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.2 + 0.3,
                  }}
                >
                  <Card
                    sx={{
                      minHeight: 450,
                      borderRadius: 4,
                      boxShadow: `0 16px 32px ${alpha(plan.color, 0.3)}`,
                      borderTop: `6px solid ${plan.color}`,
                      background: `linear-gradient(180deg, ${alpha(
                        theme.palette.background.default,
                        0.7
                      )} 0%, ${alpha(
                        theme.palette.background.paper,
                        0.9
                      )} 100%)`,
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-10px)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        py: 4,
                        px: 3,
                        position: "relative",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {plan.popular && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -10,
                            right: 20,
                            bgcolor: "secondary.main",
                            color: "white",
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            fontWeight: "bold",
                            fontSize: 12,
                          }}
                        >
                          POPULAR
                        </Box>
                      )}
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontWeight: 700, color: plan.color }}
                      >
                        {plan.name}
                      </Typography>
                      <Box sx={{ my: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                          {plan.price}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          per month
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1, mt: 2, mb: 3 }}>
                        {plan.features.map((feature, i) => (
                          <Typography
                            key={i}
                            variant="body1"
                            sx={{
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 8,
                                height: 8,
                                bgcolor: plan.color,
                                borderRadius: "50%",
                                mr: 2,
                              }}
                            />
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                      <Button
                        variant={plan.popular ? "contained" : "outlined"}
                        color={plan.popular ? "secondary" : "primary"}
                        size="large"
                        fullWidth
                        sx={{
                          mt: "auto",
                          borderRadius: 2,
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        Choose Plan
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      ) : (
        // Landing Page
        <Box
          sx={{
            height: "100vh",
            py: { xs: 8, sm: 10, md: 12 },
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant={isMobile ? "h3" : isTablet ? "h2" : "h1"}
                gutterBottom
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  mb: { xs: 2, md: 3 },
                  px: { xs: 0, sm: 2 },
                  background: `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.secondary.light} 90%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.03em",
                }}
              >
                Revolutionize Your Digital Marketing with WhatsApp
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h5"}
                sx={{
                  mb: { xs: 3, md: 4 },
                  px: { xs: 0, sm: 2, md: 4 },
                  lineHeight: 1.6,
                  color: theme.palette.primary.contrastText,
                }}
              >
                Leverage WhatsApp Cloud APIs for seamless, scalable marketing
                solutions. Create wallets, track usage transparently, and choose
                from flexible subscription plans.
              </Typography>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size={isMobile ? "medium" : "large"}
                  onClick={handleGetStarted}
                  sx={{
                    px: 6,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.light})`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 24px ${alpha(
                        theme.palette.secondary.main,
                        0.4
                      )}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View Pricing Plans
                </Button>
              </motion.div>
            </motion.div>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default LandingPage;
